import React from 'react';
import { Head, Link } from '@inertiajs/react';
import { Card, Badge, Statistic, MetricBar, Button } from '~/marketing/ui';
import { routes } from '~/marketing/routes';
import { withMarketingLayout } from '~/marketing/MarketingLayout';
import { METHOD_POST } from '~/marketing/blog';

/* Contract: GET /grader/:token → { report }.
   summary, recommended_nobody, caveat and engines[].detail are Class 2 and
   arrive fully written: rendered verbatim, never rebuilt here, because the
   result email carries the same sentences. named / measurable drive layout only.
   summary is null when inconclusive — that is unknown, not zero, and no path
   below can print 0% or "0 of 0". No share control: that surface is unbuilt. */

const ENGINE = { chatgpt: 'ChatGPT', claude: 'Claude', gemini: 'Gemini', perplexity: 'Perplexity', google: 'Google AI Overviews' };
const OUTCOME = {
  named: { tone: 'signal', label: 'Named' },
  not_named: { tone: 'neutral', label: 'Not named' },
  recommended_nobody: { tone: 'neutral', label: 'Recommended no business' },
  no_overview: { tone: 'unknown', label: 'No AI Overview' },
  failed: { tone: 'unknown', label: 'No answer' },
  pending: { tone: 'placeholder', label: 'Pending' },
};
/* The inconclusive sentence is now sent as `inconclusive_summary`, sanctioned
   into copy-rules.md on 2026-09-24 and rendered verbatim like the others. The
   local string that stood here is gone rather than kept as a fallback: a
   fallback for a Class 2 string is a second copy, which is the thing that let a
   retired figure keep rendering for six weeks. */

const sec = { display: 'grid', gap: 'var(--space-5)' };
const rule = { borderBottom: '1px solid var(--border-subtle)', paddingBottom: 'var(--space-3)' };
const note = { fontSize: 'var(--fs-body-sm)', color: 'var(--text-body)', maxWidth: '62ch', margin: 0 };

/* **Who was named instead**, 2026-10-14 — promised by /grader since it shipped
   and shown nowhere until then. The subject's own row sits in the list, sorted
   by count, the way the dashboard does it: a figure with nothing beside it
   cannot be read, and the list shows where they are without saying they are
   behind. Every row is out of the same answers as the figure above. */
function NamedInstead({ r }) {
  const rows = [{ name: r.company_name, named: r.named, you: true }]
    .concat(r.competitors.rows.map((c) => ({ ...c, you: false })))
    .sort((a, b) => b.named - a.named || a.name.localeCompare(b.name));
  const others = r.competitors.distinct_businesses;
  return (
    <section style={sec}>
      <div style={rule}><h2 style={{ fontSize: 'var(--fs-h2)' }}>Who else was named</h2></div>
      <p style={note}>{others === 1 ? 'One other business was named across these answers.' : `${others} other businesses were named across these answers.`}</p>
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        {rows.map((row) => (
          <MetricBar key={row.you ? '__you__' : row.name} name={row.name} value={row.named} max={r.measurable}
            display={row.named + ' of ' + r.measurable} tone={row.you ? 'signal' : 'muted'} />
        ))}
      </div>
      <p className="sp-caption" style={{ margin: 0, maxWidth: '62ch' }}>
        Counted over the same answers as your own figure, so the rows are comparable. A business named twice in one answer is counted once, the same way you are.
      </p>
    </section>
  );
}

/* **Which sources the answers drew on**, the other half of that promise,
   named rather than counted: five answers cite few enough sites to list. The
   two groups are the dashboard's and are described in its words, because
   getting listed and writing a page are different jobs. */
function SiteGroup({ title, body, sites, total, children }) {
  if (!total) return null;
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)', alignContent: 'start' }}>
      <h3 style={{ fontSize: 'var(--fs-h4)' }}>{title}</h3>
      <p style={note}>{body}</p>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
        {sites.map((s) => (
          <li key={s.domain} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, borderTop: '1px solid var(--border-subtle)', paddingTop: 6 }}>
            <span className="sp-mono" style={{ fontSize: 13, color: 'var(--text-strong)', overflowWrap: 'anywhere' }}>{s.domain}</span>
            <span className="sp-mono" style={{ fontSize: 12, color: 'var(--text-muted)', flex: 'none' }}>{s.citations === 1 ? 'cited once' : `cited ${s.citations} times`}</span>
          </li>
        ))}
      </ul>
      {total > sites.length && <p className="sp-caption" style={{ margin: 0 }}>And {total - sites.length} more.</p>}
      {children}
    </div>
  );
}

function Sources({ s }) {
  return (
    <section style={sec}>
      <div style={rule}><h2 style={{ fontSize: 'var(--fs-h2)' }}>Where the answers came from</h2></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(300px,100%),1fr))', gap: 'var(--space-8)' }}>
        <SiteGroup title="Places that can list you" sites={s.listings} total={s.listings_total}
          body="Directories, review sites, forums, news and reference pages the answers drew on. A business can appear on these.">
          <p className="sp-caption" style={{ margin: 0 }}><Link href="/blog/get-listed-where-ai-assistants-look">How to get listed where AI assistants look</Link></p>
        </SiteGroup>
        <SiteGroup title="Businesses’ own pages" sites={s.content} total={s.content_total}
          body="The answers also drew on businesses’ own websites. A page there that covers something yours doesn’t is a page worth writing." />
      </div>
    </section>
  );
}

export default function Report({ report }) {
  const r = report;
  const unknown = r.inconclusive || r.summary == null || !r.measurable;
  return (
    <>
      <Head title={'AI visibility check · ' + r.company_name}>
        <meta name="robots" content="noindex, nofollow" />
        {/* The token is in the URL; keep it out of Referer on every outbound link. */}
        <meta name="referrer" content="no-referrer" />
      </Head>
      <main className="sp-page" style={{ paddingBlock: 'clamp(32px,4vw,56px)', display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 'var(--space-12)' }}>
        <header style={{ display: 'grid', gap: 'var(--space-4)' }}>
          <span className="sp-label">Free AI visibility check · private</span>
          <h1 style={{ fontSize: 'var(--fs-h1)', lineHeight: 1.08 }}>{r.company_name}</h1>
          <span className="sp-mono" style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-body)' }}>{r.trade} · {r.city}, {r.region} · checked {r.checked_on}</span>
          {/* The words every AI assistant was given, so the finding below can be
              read against them. Nil only for a check with no run. */}
          {r.question && <p style={{ color: 'var(--text-body)', maxWidth: '62ch', margin: 0 }}>We asked each AI assistant: “{r.question}”</p>}
        </header>

        <Card variant={unknown ? 'default' : 'signal'}>
          <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
            {unknown ? (
              <>
                <Statistic label="Named in" state="unknown" />
                <p style={{ color: 'var(--text-body)', maxWidth: '62ch', margin: 0 }}>{r.inconclusive_summary}</p>
              </>
            ) : (
              <>
                {/*
                  **The figure renders only where it *is* the sentence.** This
                  block drew the `Statistic` and the `MetricBar`
                  unconditionally, so a check that named nobody showed
                  "Named in / 0 / of 1 answers" and a bar reading "0 of 1"
                  directly above "Not named in the one answer that recommended
                  a business." Found 2026-09-29 by walking the flow.

                  `show_figure` is `VisibilitySummary#figure?` — the rule
                  extracted on 2026-09-25 and wired into the dashboard and the
                  first-result email, but not into this page, which is the one
                  it was written for. A grader check is one question across five
                  engines, so a measurable of 1 with `named` at 0 is the routine
                  case, not an edge one, and this is a stranger's first sight of
                  the product.

                  When the figure is suppressed the sentence carries the finding
                  on its own, set larger so the card still has a subject — the
                  same shape the dashboard uses.
                */}
                {r.show_figure ? (
                  <>
                    <Statistic label="Named in" value={r.named} of={r.measurable} noun="answers" tone="signal" size="xl" />
                    <MetricBar value={r.named} max={r.measurable} display={r.named + ' of ' + r.measurable} tone="signal" />
                    <p style={{ color: 'var(--text-body)', maxWidth: '62ch', margin: 0 }}>{r.summary}</p>
                  </>
                ) : (
                  <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 600, lineHeight: 1.25, maxWidth: '46ch', margin: 0, color: 'var(--text-strong)' }}>
                    {r.summary}
                  </p>
                )}
              </>
            )}
            {r.recommended_nobody && <p style={{ color: 'var(--text-body)', maxWidth: '62ch', margin: 0 }}>{r.recommended_nobody}</p>}
            {r.caveat && <p className="sp-caption" style={{ margin: 0, maxWidth: '64ch' }}>{r.caveat}</p>}
            {/* The caveat already says one question is a snapshot; this is where to read more. */}
            <p className="sp-caption" style={{ margin: 0 }}><Link href={METHOD_POST}>How these numbers are counted</Link></p>
          </div>
        </Card>

        <section style={sec}>
          <div style={rule}><h2 style={{ fontSize: 'var(--fs-h2)' }}>By AI assistant</h2></div>
          <div style={{ display: 'grid' }}>
            {r.engines.map((e) => {
              const o = OUTCOME[e.outcome] || OUTCOME.failed;
              return (
                <div key={e.engine} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,180px) minmax(0,1fr) auto', gap: 12, alignItems: 'center', padding: '14px 0', borderTop: '1px solid var(--border-subtle)' }}>
                  <span className="sp-mono" style={{ fontSize: 13, color: 'var(--text-strong)' }}>{ENGINE[e.engine] || e.engine}</span>
                  <span style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-body)' }}>{e.detail}</span>
                  <Badge tone={o.tone}>{o.label}</Badge>
                </div>
              );
            })}
          </div>
        </section>

        {r.competitors && <NamedInstead r={r} />}
        {r.sources && <Sources s={r.sources} />}

        <section style={{ ...sec, borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-8)', justifyItems: 'start' }}>
          {/* "One question is a snapshot. Tracking makes it a measurement." until
              2026-10-26: the caveat in the card above says the snapshot half. */}
          <h2 style={{ fontSize: 'var(--fs-h2)', maxWidth: '26ch' }}>Track it every week</h2>
          <p style={{ color: 'var(--text-body)', maxWidth: '60ch', margin: 0 }}>A plan asks your industry’s questions of all five AI assistants every week, so you can see whether a change holds. 14 days free, no credit card. <Link href={routes.methodology}>How we measure</Link>.</p>
          <Button size="lg" href={routes.signUp}>Start free trial</Button>
        </section>
      </main>
    </>
  );
}

Report.layout = withMarketingLayout;
